import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import routes from "../../../../routes/routes";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { setUseCase } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import KeyHandoverServiceSelectionStep from "../steps/KeyHandoverServiceSelectionStep";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../components/ui/spinner";

/**
 * ServiceSelectionContent handles location/service hydration and displays selection UI
 */
function ServiceSelectionContent() {
    // Hydrate location from URL (requires KeyHandoverProvider context)
    const locationHydrated = useLocationHydration();

    // Hydrate service from URL (requires KeyHandoverServiceProvider context)
    const serviceHydrated = useServiceHydration();

    // Show loading spinner while location or service is hydrating
    if (!locationHydrated || !serviceHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return <KeyHandoverServiceSelectionStep />;
}

/**
 * KeyHandoverServiceSelectionPage allows users to select equipment/service for key handover
 */
const KeyHandoverServiceSelectionPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selectedGroup = useSelector((state: RootState) => state.bookingCreation.selectedGroup);

    // Initialize use case
    useEffect(() => {
        dispatch(setUseCase({ useCase: "keys" }));
    }, [dispatch]);

    // Bottom actions for service selection
    const bottomActions = (
        <KeyHandoverNavigateNextAction
            to={routes.keyHandoverSchedulePath}
            enabled={Boolean(selectedGroup?.id)}
            bounceKey={selectedGroup?.id ?? null}
        />
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.service", {
                defaultValue: "Service auswählen",
            })}
            backTo={routes.keyHandoverPaymentModelPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <ServiceSelectionContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default KeyHandoverServiceSelectionPage;
